import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { getMyShifts, type MyShiftRow } from '../../api/assignments';
import { fetchConfigCanjes, listMisCanjesDelDia, type CanjeRow, type ConfigCanjes } from '../../api/canjes';
import { getPhotoUrl } from '../../api/attendance';
import { flushCanjes, pendingCanjeCount } from '../../offline/canjeQueue';
import CanjeForm, { type ContextoCanje } from './CanjeForm';

const TZ = 'America/Lima';

const hoyLima = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
const horaLima = (iso: string): string => formatInTimeZone(new Date(iso), TZ, 'HH:mm');

/**
 * Pantalla de trabajo del mercadista: el 95% de su día es tocar "Registrar canje".
 *
 * DOS DECISIONES QUE SE VEN ACÁ
 *
 * 1. **El contador cuenta CANJES, no clientes.** Un cliente con 4 canjes suma 4, igual que el
 *    dashboard del cliente (7.676 canjes en 311 jornadas). Si contara registros, su número y el
 *    nuestro no cuadrarían nunca.
 *
 * 2. **Hay selector de día.** Es lo que hace posible el requisito de registrar a destiempo: la
 *    que sube de noche —o al día siguiente— elige la fecha y el sistema busca su turno de ESE
 *    día. Sin el selector, quien no alcanzó a registrar en el momento no tendría por dónde.
 *    La bandera de tardío la pone el servidor de todas formas: la fecha declarada no borra
 *    la hora real del registro.
 */
/**
 * Miniatura de la boleta en la lista del día. Pedido del usuario: "que se vea en chiquito la
 * foto, que se vea bonito".
 *
 * La URL firmada dura 60 s, así que se pide por miniatura y no todas de golpe. A diferencia del
 * historial —que puede traer 30 días y allí se usa un IntersectionObserver— acá la lista es la
 * de UNA jornada: con una meta de ~20 canjes, pedirlas al montar es una llamada por fila y la
 * lista se ve entera de una pasada.
 *
 * Si la firma falla o la imagen no carga, NO se deja un ícono roto: se dice con palabras. Ver
 * una evidencia rota es peor que no verla, porque hace dudar del registro que sí se hizo.
 */
function MiniaturaBoleta({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [falla, setFalla] = useState(false);

  useEffect(() => {
    let vivo = true;
    void getPhotoUrl(path)
      .then((u) => { if (vivo) setUrl(u); })
      .catch(() => { if (vivo) setFalla(true); });
    return () => { vivo = false; };
  }, [path]);

  if (falla) {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded border bg-gray-50 text-[10px] text-gray-500">
        sin vista
      </div>
    );
  }
  if (!url) {
    return <div className="h-14 w-14 shrink-0 animate-pulse rounded border bg-gray-100" />;
  }
  return (
    <img
      src={url}
      alt="Boleta del canje"
      onError={() => setFalla(true)}
      className="h-14 w-14 shrink-0 rounded border object-cover"
    />
  );
}

export default function Canjes() {
  const [fecha, setFecha] = useState(hoyLima());
  const [turno, setTurno] = useState<MyShiftRow | null>(null);
  const [config, setConfig] = useState<ConfigCanjes | null>(null);
  const [canjes, setCanjes] = useState<CanjeRow[]>([]);
  const [pendientes, setPendientes] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [registrando, setRegistrando] = useState(false);

  const recargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const turnos = await getMyShifts(fecha);
      const elegido = turnos[0] ?? null;
      setTurno(elegido);
      // Los canjes del día se leen SIEMPRE, incluso sin turno: si ya registró algo y después
      // le cambiaron la programación, esconderle lo suyo sería peor que mostrarlo.
      const mios = await listMisCanjesDelDia(fecha);
      setCanjes(mios);
      if (elegido) {
        // Con la tienda y el día, no solo la campaña: desde la 0029 una dinámica puede valer
        // solo en ciertos PDV y ciertos días, y esta pantalla solo debe ofrecer lo que hoy,
        // aquí, está vigente.
        setConfig(await fetchConfigCanjes(
          elegido.position.campaign_id, elegido.position.pdv.id, fecha,
        ));
      } else {
        setConfig(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar tu día. Intenta de nuevo.');
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { void recargar(); }, [recargar]);

  // Cuenta los que esperan señal e intenta vaciarlos al abrir: si volvió la conexión, suben
  // solos sin que ella tenga que hacer nada.
  const revisarCola = useCallback(async () => {
    // TODO lo de la cola local va dentro del try, contador incluido: si la base local no
    // responde —esquema bloqueado por otra pestaña, almacenamiento lleno—, esta pantalla tiene
    // que seguir funcionando. Lo guardado no se pierde por no poder contarlo.
    try {
      await flushCanjes();
      setPendientes(await pendingCanjeCount());
    } catch {
      setPendientes(0);
    }
  }, []);

  useEffect(() => { void revisarCola(); }, [revisarCola]);

  const totalCanjes = useMemo(
    () => canjes.filter((c) => c.estado !== 'anulado').reduce((s, c) => s + c.totalCanjes, 0),
    [canjes],
  );

  const contexto: ContextoCanje | null = turno
    ? {
        campaignId: turno.position.campaign_id,
        pdvId: turno.position.pdv.id,
        assignmentId: turno.id,
        fecha,
      }
    : null;

  async function alGuardar(nombreCliente: string): Promise<void> {
    setRegistrando(false);
    setAviso(`Canje de ${nombreCliente} guardado.`);
    await revisarCola();
    await recargar();
  }

  if (registrando && config && contexto) {
    return (
      <CanjeForm
        config={config}
        contexto={contexto}
        onGuardado={(nombre) => { void alGuardar(nombre); }}
        onCancelar={() => setRegistrando(false)}
      />
    );
  }

  return (
    <div className="space-y-4 p-4">
      <header className="rounded border border-green-300 bg-green-50 p-3">
        {turno ? (
          <>
            <p className="font-medium text-green-900">
              ● En turno · {turno.position.pdv.name}
            </p>
            <p className="text-sm text-green-800">
              {turno.position.start_time.slice(0, 5)}–{turno.position.end_time.slice(0, 5)}
              {' · '}{turno.position.campaign.brand}
            </p>
          </>
        ) : (
          <p className="font-medium text-gray-700">Canjes</p>
        )}
      </header>

      <label className="block">
        <span className="text-sm text-gray-700">Día de los canjes</span>
        <input
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          className="mt-1 block w-full rounded border px-3 py-2"
        />
        <span className="text-xs text-gray-500">
          Si estás subiendo canjes de otro día, cámbialo aquí.
        </span>
      </label>

      {aviso && (
        <p className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{aviso}</p>
      )}
      {error && (
        <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}
      {pendientes > 0 && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {pendientes} esperando señal. Se suben solos cuando vuelva la conexión.
        </p>
      )}

      {cargando && <p className="text-gray-500">Cargando tu día…</p>}

      {!cargando && !turno && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          No tienes turno programado ese día. Avísale a tu coordinador para que lo registre, y
          después vuelve a entrar: tus canjes se guardan igual, pero el mercado sale de tu turno.
        </p>
      )}

      {!cargando && turno && config && (
        <>
          <section className="rounded border p-4 text-center">
            <p className="text-2xl font-semibold">{totalCanjes} canjes hoy</p>
            {config.dailyGoal !== null && (
              <p className="text-sm text-gray-600">meta {config.dailyGoal}</p>
            )}
            <button
              type="button"
              onClick={() => { setAviso(''); setRegistrando(true); }}
              className="mt-3 min-h-[56px] w-full rounded bg-blue-600 px-4 py-3 text-lg font-medium text-white"
            >
              + Registrar canje
            </button>
          </section>

          <section>
            <h2 className="mb-2 font-medium">Registrado</h2>
            {canjes.length === 0 && (
              <p className="text-sm text-gray-500">
                Todavía no registraste canjes en ese día.
              </p>
            )}
            <ul className="space-y-2">
              {canjes.map((c) => (
                <li key={c.id} className="flex gap-3 rounded border p-3 text-sm">
                  {c.fotoBoletaPath && <MiniaturaBoleta path={c.fotoBoletaPath} />}
                  <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    {/* Sin nombre de cliente (el raspa y gana no lo pide) manda el premio, que
                        es lo que de verdad identifica el registro para quien lo hizo. */}
                    <span className="font-medium">
                      {c.clienteNombre ?? c.lineas.flatMap((l) => l.premios.map((p) => p.prizeName)).join(' + ') ?? 'Canje'}
                    </span>
                    <span className="text-gray-500">{horaLima(c.registradoEn)}</span>
                  </div>
                  <p className="text-gray-700">
                    {c.lineas.map((l) => `${l.dynamicName} ×${l.canjes}`).join(' · ')}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.estado === 'anulado' && (
                      <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">anulado</span>
                    )}
                    {c.registroTardio && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">tardío</span>
                    )}
                    {c.sinAsistencia && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        sin asistencia
                      </span>
                    )}
                    {c.fueraDeProgramacion && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        fuera de programación
                      </span>
                    )}
                  </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Vuelve a "Mi turno", que es donde está el botón de marcar salida.
              Antes decía "Cerrar turno del día" y llevaba directo a la pantalla de marcar: el
              nombre prometía cerrar el turno y lo que aparecía era otra cosa. Ahora dice a dónde
              va, y desde allí la persona decide si marca la salida o sigue registrando. */}
          <Link
            to="/movil"
            className="block min-h-[44px] rounded border py-3 text-center text-sm text-gray-700"
          >
            Volver a mi turno
          </Link>
        </>
      )}
    </div>
  );
}
